import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  FILTERS,
  OUTCOMES,
  OUTCOME_LABEL,
  PARTY_LABEL,
  STATE_META,
  fetchFollowUp,
  filterCounts,
  saveFollowUp,
  stateOf,
} from "./followUpUtils";

const ago = (d) => (d ? moment(d).fromNow() : "");

// Search box + "To call / Not contacted / …" chips with live counts.
export function FollowUpFilterBar({ rows, search, onSearch, filter, onFilter }) {
  const counts = filterCounts(rows);
  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ md: "center" }}>
      <TextField
        size="small"
        placeholder="Search name, phone, email or property"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        sx={{ minWidth: { md: 300 }, bgcolor: "#fff" }}
      />
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const meta = STATE_META[f.key];
          return (
            <Chip
              key={f.key}
              clickable
              onClick={() => onFilter(f.key)}
              label={`${f.label} (${counts[f.key]})`}
              variant={active ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                ...(active
                  ? { bgcolor: meta?.bg || "#1f2937", color: meta?.color || "#fff", borderColor: meta?.border }
                  : { bgcolor: "#fff" }),
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

// The cell that goes in a table row: the colour-coded state, when it was last
// called and by whom, and — when two people must be contacted — one mini chip
// for each so it is obvious who is still outstanding.
export function FollowUpStatus({ row, onOpen, disabled }) {
  const fu = row.followUp;
  const meta = STATE_META[stateOf(row)];
  const parties = fu ? Object.entries(fu.parties) : [];
  const lastOverall = parties
    .map(([, p]) => p.last)
    .filter(Boolean)
    .sort((a, b) => new Date(b.at) - new Date(a.at))[0];

  return (
    <Box sx={{ minWidth: 150 }}>
      <Chip size="small" label={meta.label} sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }} />
      {parties.length > 1 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          {parties.map(([party, p]) => (
            <Chip
              key={party}
              size="small"
              label={`${PARTY_LABEL[party]} ${p.state === "not_contacted" ? "✗" : p.state === "due" ? "!" : "✓"}`}
              sx={{ bgcolor: STATE_META[p.state].bg, color: STATE_META[p.state].color, height: 20, fontSize: 11 }}
            />
          ))}
        </Stack>
      )}
      {lastOverall ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {OUTCOME_LABEL[lastOverall.outcome]} · {ago(lastOverall.at)}
          {lastOverall.byName ? ` · ${lastOverall.byName}` : ""}
          {fu.attempts > 1 ? ` · ${fu.attempts} tries` : ""}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block">
          Nobody has called yet
        </Typography>
      )}
      <Button size="small" sx={{ px: 0, minWidth: 0 }} onClick={onOpen} disabled={disabled}>
        Details / Log call
      </Button>
    </Box>
  );
}

const outcomeHint = { no_answer: 1, promised: 2, reached: 3 };

// Everything about one row in one place: the full detail, how to reach each
// person, the call history, and the form to log a new call.
//
// details:  [{ label, value }]      contacts: [{ party, name, phone, email }]
// followUp: the row's summary (its scope/subjectId say what is being logged)
// canLog:   false for a view-only admin
export function FollowUpDialog({ open, onClose, title, subtitle, details = [], contacts = [], followUp, canLog = true, onSaved }) {
  const parties = followUp ? Object.keys(followUp.parties) : [];
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [party, setParty] = useState("");
  const [outcome, setOutcome] = useState("reached");
  const [note, setNote] = useState("");
  const [next, setNext] = useState("");

  const scope = followUp?.scope;
  const subjectId = followUp?.subjectId;

  useEffect(() => {
    if (!open || !scope) return;
    let live = true;
    setLoading(true);
    setError("");
    setNote("");
    setNext("");
    setOutcome("reached");
    // Start on whichever person still has to be called.
    const first = Object.entries(followUp.parties).find(
      ([, p]) => p.state === "not_contacted" || p.state === "due"
    );
    setParty(first ? first[0] : parties[0]);
    fetchFollowUp(scope, subjectId)
      .then((d) => live && setHistory(d.history))
      .catch(() => live && setError("Could not load the call history."))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope, subjectId]);

  const submit = async () => {
    if (outcome === "promised" && !next) {
      setError("Pick the date they promised to act by.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const d = await saveFollowUp({
        scope,
        subjectId,
        party,
        outcome,
        note,
        nextFollowUpAt: next ? new Date(`${next}T09:00:00`).toISOString() : undefined,
      });
      setHistory(d.history);
      setNote("");
      setNext("");
      onSaved?.(d.followUp);
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || "Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {title}
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {details.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {details.map((d) => (
              <Grid item xs={6} sm={4} key={d.label}>
                <Typography variant="caption" color="text.secondary">
                  {d.label}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {d.value || "—"}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}

        {contacts.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Who to contact
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {contacts.map((c) => {
                const p = followUp?.parties?.[c.party];
                const meta = STATE_META[p?.state || "not_contacted"];
                return (
                  <Box
                    key={c.party}
                    sx={{ p: 1.25, border: "1px solid #e5e7eb", borderLeft: `4px solid ${meta.border}`, borderRadius: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700}>
                        {PARTY_LABEL[c.party]}: {c.name || "—"}
                      </Typography>
                      <Chip size="small" label={meta.label} sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />
                    </Stack>
                    <Typography variant="body2">
                      {c.phone ? <Link href={`tel:${c.phone}`}>{c.phone}</Link> : "No phone"}
                      {c.email ? (
                        <>
                          {" · "}
                          <Link href={`mailto:${c.email}`}>{c.email}</Link>
                        </>
                      ) : null}
                    </Typography>
                    {p?.nextFollowUpAt && (
                      <Typography variant="caption" color="text.secondary">
                        Follow up on {moment(p.nextFollowUpAt).format("DD MMM YYYY")}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" gutterBottom>
          Call history
        </Typography>
        {loading ? (
          <CircularProgress size={22} />
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No one has contacted them yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {history.map((h) => (
              <Box key={h.id} sx={{ p: 1, bgcolor: "#f8fafc", borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{PARTY_LABEL[h.party]}</strong> — {OUTCOME_LABEL[h.outcome]}
                </Typography>
                {h.note && <Typography variant="body2">{h.note}</Typography>}
                <Typography variant="caption" color="text.secondary">
                  {moment(h.at).format("DD MMM YYYY, h:mm A")} · {h.byName || "Admin"}
                  {h.nextFollowUpAt ? ` · next: ${moment(h.nextFollowUpAt).format("DD MMM")}` : ""}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {canLog && followUp && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Log a call
            </Typography>
            {parties.length > 1 && (
              <ToggleButtonGroup exclusive size="small" value={party} onChange={(e, v) => v && setParty(v)} sx={{ mb: 1.5 }}>
                {parties.map((p) => (
                  <ToggleButton key={p} value={p} sx={{ textTransform: "none" }}>
                    {PARTY_LABEL[p]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            <Box sx={{ mb: 1.5 }}>
              <ToggleButtonGroup exclusive size="small" value={outcome} onChange={(e, v) => v && setOutcome(v)} sx={{ flexWrap: "wrap" }}>
                {OUTCOMES.map((o) => (
                  <ToggleButton key={o.key} value={o.key} sx={{ textTransform: "none" }}>
                    {o.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note (optional)"
                  placeholder="e.g. will pay on Friday, asked to call after 5pm"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              {outcome !== "closed" && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={outcome === "promised" ? "Promised date" : "Follow up on"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: moment().format("YYYY-MM-DD") }}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    helperText={next ? "" : `Blank = ${outcomeHint[outcome]} day${outcomeHint[outcome] > 1 ? "s" : ""} from now`}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        {canLog && followUp && (
          <Button variant="contained" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save call"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
